import { inject } from '@angular/core';
import {
  CanActivateFn,
  CanActivateChildFn,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  ActivatedRoute,
} from '@angular/router';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { MsalService } from '@azure/msal-angular';
import { of, switchMap } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { state } from '@angular/animations';

export function checkPublicPage(): boolean {
  const modulesStore = inject(ModulesStore);

  // Use current browser URL instead of router state
  const currentUrl = window.location.pathname;

  // Strip query parameters if present (just in case)
  const urlWithoutParams = currentUrl.split('?')[0];

  // Get the first segment of the URL which should be the module slug
  const urlSegments = urlWithoutParams.split('/').filter(segment => segment);
  const moduleSlug = urlSegments[0] || ''; // First non-empty segment

  if (!moduleSlug) {
    return false;
  }

  const module = modulesStore.getModuleBySlug(moduleSlug);
  return module?.isPublic ?? false;
}

export function isPublicPage(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
  const modulesStore = inject(ModulesStore);

  // Get the first segment of the URL which should be the module slug
  const urlSegments = state.url.split('/');
  const moduleSlug = urlSegments[1] || ''; // First segment after the leading slash

  console.log('Checking if module is public:', moduleSlug);
  const module = modulesStore.getModuleBySlug(moduleSlug);
  return module?.isPublic || false;
}

export const PublicPageGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const msalService = inject(MsalService);

  // If the page is public, allow access without MSAL or other checks
  if (isPublicPage(route, state)) {
    return true;
  }

  // If it's not a public page, perform the normal MSAL and Auth checks
  if (!msalService.instance.getAllAccounts().length) {
    return router.parseUrl('/home');
  }

  // Check the authentication status using the AuthService
  return authService.check().pipe(
    switchMap(authenticated => {
      if (!authenticated) {
        return of(router.parseUrl('/home'));
      }
      return of(true);
    })
  );
};

export const PublicPageChildGuard: CanActivateChildFn = (childRoute, state) => {
  // For child routes, use the same logic but also check the parent route
  const parentSnapshot = childRoute.parent;

  // If the parent route is public, allow access
  if (parentSnapshot && isPublicPage(parentSnapshot, state)) {
    return true;
  }

  // Otherwise, the same check as for the main route
  return PublicPageGuard(childRoute, state);
};
